/*
 * The pages being edited, for the leave guard (`app/EditGuard.tsx`). Each
 * edit session (`EditSessionProvider`) registers while mounted with how to
 * tell whether anything is unsaved and how to send it.
 */
export interface GuardedSession {
  /** Something is queued, saving, failed, offline or in conflict. */
  dirty: () => boolean;
  /** Sends everything queued; true when nothing is left unsaved. */
  flush: () => Promise<boolean>;
}

const sessions = new Set<GuardedSession>();
let excused = false;

/** The next move away goes without asking: the page it leaves is gone. */
export const excuseNextLeave = (): void => {
  excused = true;
};

/** True once after `excuseNextLeave`. */
export function takeExcuse(): boolean {
  const was = excused;
  excused = false;
  return was;
}

export function guardSession(session: GuardedSession): () => void {
  sessions.add(session);
  return () => void sessions.delete(session);
}

export const anyDirty = (): boolean => [...sessions].some((s) => s.dirty());

/** Sends every session's queue, waiting at most `ms`; true when all of it saved in time. */
export async function flushAll(ms: number): Promise<boolean> {
  const all = Promise.all([...sessions].map((s) => s.flush().catch(() => false))).then((done) => done.every(Boolean));
  const late = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), ms));
  return Promise.race([all, late]);
}
